import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress
} from '@mui/material';
import { 
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import '../assets/css/futuristic-modern.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Invalid reset link. No token provided.');
      setVerifying(false);
    } else {
      setToken(tokenParam);
      setVerifying(false);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Validate passwords
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }
    
    try {
      await axios.post(`${API_BASE_URL}/api/reset-password`, { 
        token,
        password 
      });
      setSuccess(true);
      // Redirect to signin after 2 seconds
      setTimeout(() => {
        navigate('/signin?reset=success');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress 
              size={60}
              thickness={4}
              sx={{ color: 'var(--neon-cyan)', mb: 2 }} 
            />
            <Typography 
              variant="h6" 
              sx={{ color: 'var(--text-muted)' }}
            >
              Verifying reset link...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  if (success) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Container maxWidth="sm">
          <Paper 
            elevation={24}
            sx={{
              background: 'var(--surface-2)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-lg)',
              backdropFilter: 'blur(20px)',
              padding: { xs: 4, md: 5 },
              textAlign: 'center'
            }}
          >
            <CheckCircle 
              sx={{ 
                fontSize: 80, 
                color: '#468230',
                mb: 2
              }} 
            />
            <Typography 
              variant="h4" 
              sx={{ 
                color: 'var(--text-primary)',
                fontWeight: 600,
                mb: 2
              }}
            >
              Password Reset!
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'var(--text-muted)',
                mb: 3
              }}
            >
              Your password has been reset successfully. Redirecting to sign in...
            </Typography>
            <CircularProgress size={24} sx={{ color: 'var(--neon-cyan)' }} />
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'var(--surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={24}
          sx={{
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(20px)',
            padding: { xs: 4, md: 5 }
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 70,
                height: 70,
                margin: '0 auto',
                mb: 2,
                borderRadius: '50%',
                background: 'var(--accent-gradient)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LockIcon sx={{ fontSize: 35, color: 'white' }} />
            </Box>
            
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: 'var(--text-primary)',
                fontWeight: 600,
                mb: 1
              }}
            >
              Reset Password
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'var(--text-muted)',
                lineHeight: 1.6
              }}
            >
              Enter your new password below
            </Typography>
          </Box>

          {/* Error Message */}
          {error && (
            <Alert 
              severity="error"
              sx={{ 
                mb: 3,
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                color: '#ff6b6b'
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--surface)',
                  '& fieldset': {
                    borderColor: 'var(--border)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--neon-cyan)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--neon-cyan)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--text-muted)',
                  '&.Mui-focused': {
                    color: 'var(--neon-cyan)',
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'var(--text-muted)' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--surface)',
                  '& fieldset': {
                    borderColor: 'var(--border)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--neon-cyan)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--neon-cyan)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--text-muted)',
                  '&.Mui-focused': {
                    color: 'var(--neon-cyan)',
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: 'var(--text-muted)' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              size="large"
              disabled={loading}
              className="btn-modern-success"
              sx={{
                padding: '14px',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 'var(--radius-md)',
                background: 'linear-gradient(135deg, #468230, #00e676)',
                color: 'white',
                boxShadow: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #3a6e28, #00c966)',
                  boxShadow: 'none'
                },
                '&:disabled': {
                  opacity: 0.6
                }
              }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword;



